// Business › Reports (BL-007 slice 2; revenue by month, history fix 1): the ServiceTitan reports the
// office runs. Each is a table with its totals and "Download CSV", reading the same answer Claude gets;
// a report shows only to whoever holds the permission that reads it.

import { useState } from 'react';
import { client } from '../api.ts';
import { Header, type NavProps } from '../Nav.tsx';
import { useDesign } from '../blueprint/design.ts';
import { Empty, Loading, Money, Panel, errorText, useQuery, useToast } from '../books/ui.tsx';
import { dayText, monthName, monthOf, todayIn } from '../books/model.ts';
import { stepMonth } from '../huddle/huddle-model.ts';
import { BOOKS_INCOME, REVENUE_WORDS } from '../shared/revenue-words.ts';
import { scrollFocus } from '../shared/scrollFocus.ts';
import { CHANNEL_WORDS, type Channel } from '../settings/campaigns-model.ts';
import {
  CAMPAIGN_GROUPS,
  campaignRowLabel,
  download,
  hoursText,
  monthWords,
  rateText,
  reportsFor,
  roasText,
  yearOf,
  type CampaignGroup,
  type Report,
} from './reports-model.ts';
import '../books/books.css';
import '../books/blueprint.css';
import './reports.css';
import './blueprint.css';

interface Props {
  tz: string;
  toast: (m: string) => void;
}

export function ReportsScreen({ nav, held }: { nav: NavProps; held: readonly string[] }) {
  const mine = reportsFor(held);
  const [report, setReport] = useState<Report>(mine[0]?.report ?? 'aging');
  const [toast, setToast] = useToast();
  const here = mine.find((r) => r.report === report) ?? mine[0];
  const props: Props = {
    tz: nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    toast: setToast,
  };
  return (
    <div className="app books office-reports">
      <Header nav={nav} title={here?.label ?? 'Reports'} lede={here?.lede ?? ''} />
      <div className="books-body">
        <nav className="books-parts" aria-label="Reports">
          {mine.map((r) => (
            <button
              key={r.report}
              className={`btn quiet${report === r.report ? ' on' : ''}`}
              aria-current={report === r.report ? 'page' : undefined}
              onClick={() => setReport(r.report)}
            >
              {r.label}
            </button>
          ))}
        </nav>
        <main className="books-main">
          <div className="books-page">
            {report === 'payments' && <Payments {...props} />}
            {report === 'aging' && <Aging {...props} />}
            {report === 'revenue' && <Revenue {...props} />}
            {report === 'timesheets' && <Timesheets {...props} />}
            {report === 'pays' && <Pays {...props} />}
            {report === 'calls' && <Calls {...props} />}
            {report === 'campaigns' && <Campaigns {...props} />}
          </div>
        </main>
      </div>
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}

/** The month a range report covers, stepped a month at a time. */
function useMonth(tz: string) {
  const [[from, to], setRange] = useState(monthOf(todayIn(tz)));
  const picker = (
    <div className="seg" role="group" aria-label="Month">
      <button className="btn quiet" onClick={() => setRange(stepMonth(from, -1))}>
        ‹ {monthName(stepMonth(from, -1)[0]).split(' ')[0]}
      </button>
      <span className="month-now">{monthName(from)}</span>
      <button className="btn quiet" onClick={() => setRange(stepMonth(from, 1))}>
        {monthName(stepMonth(from, 1)[0]).split(' ')[0]} ›
      </button>
    </div>
  );
  return { from, to, picker };
}

/** "Download CSV": the same report again, with its file. */
function CsvButton({ action, input, toast }: { action: string; input: unknown; toast: (m: string) => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      className="btn soft"
      disabled={busy}
      onClick={() => {
        setBusy(true);
        void client
          .call<{ file?: { fileName: string; csv: string } }>(action, { ...(input as object), csv: true })
          .then((r) => r.file && download(r.file))
          .catch((e: unknown) => toast(errorText(e)))
          .finally(() => setBusy(false));
      }}
    >
      Download CSV
    </button>
  );
}

function Payments({ tz, toast }: Props) {
  const m = useMonth(tz);
  const q = useQuery<{
    rows: { label: string; count: number; receivedCents: number; refundedCents: number; netCents: number }[];
    total: { count: number; receivedCents: number; refundedCents: number; netCents: number };
  }>('reports.paymentTypes', { from: m.from, to: m.to });
  return (
    <Panel
      title={`Payments by type · ${monthName(m.from)}`}
      aside={
        <div className="report-tools">
          {m.picker}
          <CsvButton action="reports.paymentTypes" input={{ from: m.from, to: m.to }} toast={toast} />
        </div>
      }
    >
      {!q.data ? (
        <Loading error={q.error} what="the payments" kind="table" retry={() => void q.reload()} />
      ) : !q.data.rows.length ? (
        <Empty>No payments this month.</Empty>
      ) : (
        <div className="table-wrap" ref={scrollFocus}>
          <table className="grid-table report">
            <thead>
              <tr>
                <th>Type</th>
                <th className="num">Payments</th>
                <th className="num">Received</th>
                <th className="num">Refunded</th>
                <th className="num">Net</th>
              </tr>
            </thead>
            <tbody>
              {q.data.rows.map((r) => (
                <tr key={r.label}>
                  <td>{r.label}</td>
                  <td className="num">{r.count}</td>
                  <td className="num">
                    <Money cents={r.receivedCents} />
                  </td>
                  <td className="num">{r.refundedCents ? <Money cents={-r.refundedCents} /> : '—'}</td>
                  <td className="num">
                    <Money cents={r.netCents} />
                  </td>
                </tr>
              ))}
              <tr className="sum strong">
                <td>Total</td>
                <td className="num">{q.data.total.count}</td>
                <td className="num">
                  <Money cents={q.data.total.receivedCents} />
                </td>
                <td className="num">
                  <Money cents={-q.data.total.refundedCents} />
                </td>
                <td className="num">
                  <Money cents={q.data.total.netCents} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function Aging({ tz, toast }: Props) {
  // in Blueprint the ages are the section's facts strip, not cards inside it
  const tile = useDesign() === 'blueprint' ? 'tile' : 'sheet tile';
  const [basis, setBasis] = useState<'invoice' | 'due'>('invoice');
  const asOf = todayIn(tz);
  const q = useQuery<{
    title: string;
    buckets: { key: string; label: string; count: number; cents: number }[];
    customers: {
      customerId: string;
      customerName: string;
      cents: Record<string, number>;
      totalCents: number;
    }[];
    totalCents: number;
  }>('reports.arAging', { asOf, basis });
  return (
    <Panel
      title={q.data?.title ?? 'AR aging'}
      aside={
        <div className="report-tools">
          <div className="seg" role="group" aria-label="Aged by">
            {(['invoice', 'due'] as const).map((b) => (
              <button
                key={b}
                className={`btn quiet${basis === b ? ' on' : ''}`}
                aria-pressed={basis === b}
                onClick={() => setBasis(b)}
              >
                {b === 'invoice' ? 'By invoice date' : 'By due date'}
              </button>
            ))}
          </div>
          <CsvButton action="reports.arAging" input={{ asOf, basis }} toast={toast} />
        </div>
      }
    >
      {!q.data ? (
        <Loading error={q.error} what="the aging" kind="table" retry={() => void q.reload()} />
      ) : (
        <>
          <div className="tiles">
            {q.data.buckets.map((b) => (
              <div key={b.key} className={tile}>
                <span className="tile-label">
                  {b.label} · {b.count}
                </span>
                <Money cents={b.cents} className="big" />
              </div>
            ))}
          </div>
          {!q.data.customers.length ? (
            <Empty>Nothing is owed.</Empty>
          ) : (
            <div className="table-wrap" ref={scrollFocus}>
              <table className="grid-table report">
                <thead>
                  <tr>
                    <th>Customer</th>
                    {q.data.buckets.map((b) => (
                      <th key={b.key} className="num">
                        {b.label}
                      </th>
                    ))}
                    <th className="num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.customers.map((c) => (
                    <tr key={c.customerId}>
                      <td>{c.customerName}</td>
                      {q.data!.buckets.map((b) => (
                        <td key={b.key} className="num">
                          {c.cents[b.key] ? <Money cents={c.cents[b.key]!} /> : '—'}
                        </td>
                      ))}
                      <td className="num">
                        <Money cents={c.totalCents} />
                      </td>
                    </tr>
                  ))}
                  <tr className="sum strong">
                    <td>Total</td>
                    {q.data.buckets.map((b) => (
                      <td key={b.key} className="num">
                        <Money cents={b.cents} />
                      </td>
                    ))}
                    <td className="num">
                      <Money cents={q.data.totalCents} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}

/** Completed revenue before tax by month, the 18 months through this one (history fix 1). */
function Revenue({ toast }: Props) {
  const q = useQuery<{
    title: string;
    months: { month: string; invoices: number; revenueCents: number }[];
    totalCents: number;
    undated: { invoices: number; revenueCents: number };
  }>('reports.revenueByMonth', {});
  return (
    <Panel
      title={q.data?.title ?? 'Revenue by month'}
      aside={
        <div className="report-tools">
          <CsvButton action="reports.revenueByMonth" input={{}} toast={toast} />
        </div>
      }
    >
      {!q.data ? (
        <Loading error={q.error} what="the revenue" kind="table" retry={() => void q.reload()} />
      ) : (
        <>
          {/* what it counts, in the words Performance and the huddle use (QA-714) */}
          <p className="muted small">
            {REVENUE_WORDS} {BOOKS_INCOME}
          </p>
          {q.data.undated.invoices > 0 && (
            <p className="muted small">
              {q.data.undated.invoices} {q.data.undated.invoices === 1 ? 'invoice has' : 'invoices have'} no
              date, so {q.data.undated.invoices === 1 ? 'it’s' : 'they’re'} in no month:{' '}
              <Money cents={q.data.undated.revenueCents} /> before tax.
            </p>
          )}
          <div className="table-wrap" ref={scrollFocus}>
            <table className="grid-table report">
              <thead>
                <tr>
                  <th>Month</th>
                  <th className="num">Invoices</th>
                  <th className="num">Revenue before tax</th>
                </tr>
              </thead>
              <tbody>
                {[...q.data.months].reverse().map((r) => (
                  <tr key={r.month}>
                    <td>{monthWords(r.month)}</td>
                    <td className="num">{r.invoices}</td>
                    <td className="num">
                      {r.invoices || r.revenueCents ? <Money cents={r.revenueCents} /> : '—'}
                    </td>
                  </tr>
                ))}
                <tr className="sum strong">
                  <td>Total</td>
                  <td className="num">{q.data.months.reduce((t, r) => t + r.invoices, 0)}</td>
                  <td className="num">
                    <Money cents={q.data.totalCents} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </Panel>
  );
}

function Timesheets({ tz, toast }: Props) {
  const m = useMonth(tz);
  const q = useQuery<{
    from: string;
    to: string;
    title: string;
    rows: {
      staffId: string;
      name: string;
      regularMinutes: number;
      overtimeMinutes: number;
      doubleMinutes: number;
      statMinutes: number;
      ptoMinutes: number;
      unpaidLeaveMinutes: number;
      holidayMinutes: number;
      totalMinutes: number;
    }[];
  }>('reports.timesheets', { from: m.from, to: m.to });
  return (
    <Panel
      title={q.data?.title ?? 'Timesheets'}
      aside={
        <div className="report-tools">
          {m.picker}
          <CsvButton action="reports.timesheets" input={{ from: m.from, to: m.to }} toast={toast} />
        </div>
      }
    >
      {!q.data ? (
        <Loading error={q.error} what="the timesheets" kind="table" retry={() => void q.reload()} />
      ) : (
        <>
          {(q.data.from !== m.from || q.data.to !== m.to) && (
            <p className="muted small">
              Whole payroll weeks, so overtime and stat hours split as payroll does: {dayText(q.data.from)} to{' '}
              {dayText(q.data.to)}.
            </p>
          )}
          {!q.data.rows.length ? (
            <Empty>No hours this month.</Empty>
          ) : (
            <div className="table-wrap" ref={scrollFocus}>
              <table className="grid-table report">
                <thead>
                  <tr>
                    <th>Technician</th>
                    <th className="num">Regular</th>
                    <th className="num">Overtime 1.5×</th>
                    <th className="num">Double 2×</th>
                    <th className="num">Stat hours</th>
                    <th className="num">Time off (paid)</th>
                    <th className="num">Unpaid leave</th>
                    <th className="num">Holidays</th>
                    <th className="num">Total paid</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.rows.map((r) => (
                    <tr key={r.staffId}>
                      <td>{r.name}</td>
                      <td className="num">{hoursText(r.regularMinutes)}</td>
                      <td className="num">{hoursText(r.overtimeMinutes)}</td>
                      <td className="num">{hoursText(r.doubleMinutes)}</td>
                      <td className="num">{hoursText(r.statMinutes)}</td>
                      <td className="num">{hoursText(r.ptoMinutes)}</td>
                      <td className="num">{hoursText(r.unpaidLeaveMinutes)}</td>
                      <td className="num">{hoursText(r.holidayMinutes)}</td>
                      <td className="num">{hoursText(r.totalMinutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}

function Pays({ toast }: Props) {
  const periods = useQuery<{ periods: { id: string; startDate: string; endDate: string; status: string }[] }>(
    'payPeriods.list',
    {},
  );
  const [chosen, setChosen] = useState<string | null>(null);
  const id = chosen ?? periods.data?.periods[0]?.id ?? null;
  const q = useQuery<{
    title: string;
    prepared: boolean;
    stale: boolean;
    rows: {
      staffId: string;
      name: string;
      regularMinutes: number;
      overtimeMinutes: number;
      doubleMinutes: number;
      statMinutes: number;
      hourlyPayCents?: number;
      salaryCents?: number;
      commissionsCents?: number;
      spiffsCents?: number;
      leaveCents?: number;
      grossCents?: number;
    }[];
    total: { grossCents?: number };
  }>('reports.totalPays', id ? { periodId: id } : null);
  if (!periods.data || !periods.data.periods.length)
    return (
      <Panel title="Total pays">
        {!periods.data ? (
          <Loading error={periods.error} what="the pay periods" />
        ) : (
          <Empty>No pay periods yet.</Empty>
        )}
      </Panel>
    );
  return (
    <Panel
      title={q.data?.title ?? 'Total pays'}
      aside={
        <div className="report-tools">
          <label className="f inline">
            Pay period
            <select className="field" value={id ?? ''} onChange={(e) => setChosen(e.target.value)}>
              {periods.data.periods.map((p) => (
                <option key={p.id} value={p.id}>
                  {dayText(p.startDate)} – {dayText(p.endDate)}
                </option>
              ))}
            </select>
          </label>
          {id && <CsvButton action="reports.totalPays" input={{ periodId: id }} toast={toast} />}
        </div>
      }
    >
      {!q.data ? (
        <Loading error={q.error} what="the pays" kind="table" retry={() => void q.reload()} />
      ) : !q.data.prepared ? (
        <Empty>This period hasn’t been prepared yet: prepare it in Books › Payroll first.</Empty>
      ) : (
        <>
          {q.data.stale && <p className="notice warn small">Hours were corrected since it was prepared.</p>}
          <div className="table-wrap" ref={scrollFocus}>
            <table className="grid-table report">
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="num">Regular h</th>
                  <th className="num">OT h</th>
                  <th className="num">Double h</th>
                  <th className="num">Stat h</th>
                  <th className="num">Hourly pay</th>
                  <th className="num">Salary</th>
                  <th className="num">Commissions</th>
                  <th className="num">Spiffs</th>
                  <th className="num">Stat &amp; sick</th>
                  <th className="num">Gross</th>
                </tr>
              </thead>
              <tbody>
                {q.data.rows.map((r) => (
                  <tr key={r.staffId}>
                    <td>{r.name}</td>
                    <td className="num">{hoursText(r.regularMinutes)}</td>
                    <td className="num">{hoursText(r.overtimeMinutes)}</td>
                    <td className="num">{hoursText(r.doubleMinutes)}</td>
                    <td className="num">{hoursText(r.statMinutes)}</td>
                    {(
                      [
                        'hourlyPayCents',
                        'salaryCents',
                        'commissionsCents',
                        'spiffsCents',
                        'leaveCents',
                        'grossCents',
                      ] as const
                    ).map((k) => (
                      <td key={k} className="num">
                        <Money cents={r[k] ?? 0} />
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="sum strong">
                  <td>Total</td>
                  <td colSpan={9} />
                  <td className="num">
                    <Money cents={q.data.total.grossCents ?? 0} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </Panel>
  );
}

function Calls({ tz, toast }: Props) {
  const m = useMonth(tz);
  type Row = {
    key: string | null;
    label: string;
    taken: number;
    booked: number;
    leads: number;
    unbookedLeads: number;
    excused: number;
    underAMinute: number;
    notSet: number;
    rate: number | null;
  };
  const q = useQuery<{ rows: Row[]; total: Row; missed: number }>('reports.callReasons', {
    from: m.from,
    to: m.to,
  });
  const cells = (r: Row) => (
    <>
      <td className="num">{r.taken}</td>
      <td className="num">{r.booked}</td>
      <td className="num">{r.leads}</td>
      <td className="num">{r.unbookedLeads}</td>
      <td className="num">{r.excused}</td>
      <td className="num">{r.underAMinute}</td>
      <td className="num">{r.notSet}</td>
      <td className="num">{rateText(r.rate)}</td>
    </>
  );
  return (
    <Panel
      title={`Calls by reason · ${monthName(m.from)}`}
      aside={
        <div className="report-tools">
          {m.picker}
          <CsvButton action="reports.callReasons" input={{ from: m.from, to: m.to }} toast={toast} />
        </div>
      }
    >
      {!q.data ? (
        <Loading error={q.error} what="the calls" kind="table" retry={() => void q.reload()} />
      ) : !q.data.rows.length ? (
        <Empty>No calls this month.</Empty>
      ) : (
        <>
          {q.data.missed > 0 && (
            <p className="muted small">{q.data.missed} missed or voicemail, taken by nobody.</p>
          )}
          <div className="table-wrap" ref={scrollFocus}>
            <table className="grid-table report">
              <thead>
                <tr>
                  <th>Reason</th>
                  <th className="num">Taken</th>
                  <th className="num">Booked</th>
                  <th className="num">Leads</th>
                  <th className="num">Unbooked leads</th>
                  <th className="num">Excused</th>
                  <th className="num">Under a minute</th>
                  <th className="num">Not set</th>
                  <th className="num">Booking rate</th>
                </tr>
              </thead>
              <tbody>
                {q.data.rows.map((r) => (
                  <tr key={r.key ?? r.label}>
                    <td>{r.label}</td>
                    {cells(r)}
                  </tr>
                ))}
                <tr className="sum strong">
                  <td>Total</td>
                  {cells(q.data.total)}
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </Panel>
  );
}

interface CampaignRow {
  key: string | null;
  label: string;
  channel: string | null;
  month: string | null;
  calls: number;
  leads: number;
  unbookedLeads: number;
  booked: number;
  leadConversionRate: number | null;
  callsBooked: number;
  callBookingRate: number | null;
  jobsCompleted: number;
  invoicedJobs: number;
  revenueCents: number;
  averageTicketCents: number | null;
  /** Only for whoever sees margins (finance.margins.view); read from the work while mirroring (st-mirror §7). */
  grossProfitCents?: number | null;
  /** BL-008's ad spend in the period, each entry spread evenly over its days; estimated when part of
   *  it is a share of an imported total spread over a longer period. */
  spendCents: number;
  spendEstimated: boolean;
  costPerLeadCents: number | null;
  costPerBookedJobCents: number | null;
  roas: number | null;
}

/** reports.campaigns (e079d7fc, BL-008): a month or a year, by campaign, channel, month or both. */
function Campaigns({ tz, toast }: Props) {
  const [[monthFrom, monthTo], setMonth] = useState(monthOf(todayIn(tz)));
  const [wholeYear, setWholeYear] = useState(false);
  const [groupBy, setGroupBy] = useState<CampaignGroup>('campaign');
  const [from, to] = wholeYear ? yearOf(monthFrom) : [monthFrom, monthTo];
  const input = { from, to, groupBy };
  const q = useQuery<{
    rows: CampaignRow[];
    total: CampaignRow;
    grossProfitNote: string | null;
    /** Said only while no ad spend is recorded for the period. */
    adSpendNote: string | null;
  }>('reports.campaigns', input);
  const step = (by: 1 | -1) => {
    let r = stepMonth(monthFrom, by);
    if (wholeYear) for (let i = 1; i < 12; i++) r = stepMonth(r[0], by);
    setMonth(r);
  };
  const period = wholeYear ? monthFrom.slice(0, 4) : monthName(monthFrom);
  // the gross profit column only for whoever the server gave it to
  const margins = !!q.data && 'grossProfitCents' in q.data.total;
  const showChannel = groupBy === 'campaign' || groupBy === 'campaign_month';
  const cells = (r: CampaignRow) => (
    <>
      <td className="num">{r.calls}</td>
      <td className="num">{r.leads}</td>
      <td className="num">{r.unbookedLeads}</td>
      <td className="num">{r.booked}</td>
      <td className="num">{rateText(r.callBookingRate)}</td>
      <td className="num">{rateText(r.leadConversionRate)}</td>
      <td className="num">{r.jobsCompleted}</td>
      <td className="num">
        <Money cents={r.revenueCents} />
      </td>
      <td className="num">{r.averageTicketCents === null ? '—' : <Money cents={r.averageTicketCents} />}</td>
      {/* what it cost (BL-008): the spend, then what each lead and booked job cost, and revenue per dollar */}
      <td className="num">
        {r.spendCents || r.spendEstimated ? (
          <>
            <Money cents={r.spendCents} />
            {r.spendEstimated && <small className="per est"> est.</small>}
          </>
        ) : (
          '—'
        )}
      </td>
      <td className="num">{r.costPerLeadCents === null ? '—' : <Money cents={r.costPerLeadCents} />}</td>
      <td className="num">
        {r.costPerBookedJobCents === null ? '—' : <Money cents={r.costPerBookedJobCents} />}
      </td>
      <td className="num">{roasText(r.roas)}</td>
      {margins && (
        <td className="num">
          {r.grossProfitCents === null || r.grossProfitCents === undefined ? (
            '—'
          ) : (
            <Money cents={r.grossProfitCents} />
          )}
        </td>
      )}
    </>
  );
  return (
    <Panel
      title={`Campaigns · ${period}`}
      aside={
        <div className="report-tools">
          <div className="seg" role="group" aria-label={wholeYear ? 'Year' : 'Month'}>
            <button
              className="btn quiet"
              onClick={() => step(-1)}
              aria-label={wholeYear ? 'Previous year' : 'Previous month'}
            >
              ‹
            </button>
            <span className="month-now">{period}</span>
            <button
              className="btn quiet"
              onClick={() => step(1)}
              aria-label={wholeYear ? 'Next year' : 'Next month'}
            >
              ›
            </button>
          </div>
          <div className="seg" role="group" aria-label="Period">
            {([false, true] as const).map((y) => (
              <button
                key={String(y)}
                className={`btn quiet${wholeYear === y ? ' on' : ''}`}
                aria-pressed={wholeYear === y}
                onClick={() => setWholeYear(y)}
              >
                {y ? 'Year' : 'Month'}
              </button>
            ))}
          </div>
          <label className="f inline">
            Group
            <select
              className="field"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as CampaignGroup)}
            >
              {CAMPAIGN_GROUPS.map(([g, words]) => (
                <option key={g} value={g}>
                  {words}
                </option>
              ))}
            </select>
          </label>
          <CsvButton action="reports.campaigns" input={input} toast={toast} />
        </div>
      }
    >
      {!q.data ? (
        <Loading error={q.error} what="the campaigns" kind="table" retry={() => void q.reload()} />
      ) : !q.data.rows.length ? (
        <Empty>No calls, leads or jobs {wholeYear ? 'this year' : 'this month'}.</Empty>
      ) : (
        <>
          {(q.data.adSpendNote || q.data.grossProfitNote) && (
            <p className="muted small">
              {[q.data.adSpendNote, q.data.grossProfitNote].filter(Boolean).join(' ')}
            </p>
          )}
          <div className="table-wrap" ref={scrollFocus}>
            <table className="grid-table report">
              <thead>
                <tr>
                  <th>{groupBy === 'channel' ? 'Channel' : groupBy === 'month' ? 'Month' : 'Campaign'}</th>
                  {showChannel && <th>Channel</th>}
                  {groupBy === 'campaign_month' && <th>Month</th>}
                  <th className="num">Calls</th>
                  <th className="num">Leads</th>
                  {/* the other half of both rates, so "40 %" reads as 2 of 5 or 40 of 100 (books) */}
                  <th className="num">Unbooked leads</th>
                  <th className="num">Jobs booked</th>
                  <th className="num">Calls booked</th>
                  <th className="num">Leads that became jobs</th>
                  <th className="num">Completed</th>
                  <th className="num">Revenue</th>
                  <th className="num">
                    Average ticket <small className="per">per invoiced job</small>
                  </th>
                  <th className="num">Ad spend</th>
                  <th className="num">Cost per lead</th>
                  <th className="num">Cost per booked job</th>
                  <th className="num">
                    Return on ad spend <small className="per">revenue per $1</small>
                  </th>
                  {margins && <th className="num">Gross profit</th>}
                </tr>
              </thead>
              <tbody>
                {q.data.rows.map((r) => (
                  <tr key={`${r.key ?? 'none'}:${r.month ?? ''}`}>
                    <td>{campaignRowLabel(r, groupBy)}</td>
                    {showChannel && (
                      <td>{r.channel ? (CHANNEL_WORDS[r.channel as Channel] ?? r.channel) : '—'}</td>
                    )}
                    {groupBy === 'campaign_month' && <td className="nowrap">{monthWords(r.month)}</td>}
                    {cells(r)}
                  </tr>
                ))}
                <tr className="sum strong">
                  <td colSpan={1 + (showChannel ? 1 : 0) + (groupBy === 'campaign_month' ? 1 : 0)}>Total</td>
                  {cells(q.data.total)}
                </tr>
              </tbody>
            </table>
          </div>
          {(q.data.total.spendEstimated || q.data.rows.some((r) => r.spendEstimated)) && (
            <p className="muted small">
              est.: a share of a total spread over a longer period (an imported history), so an estimate.
            </p>
          )}
        </>
      )}
    </Panel>
  );
}
